import type {ConfiguracionDeVista, PantallaDeVista, VistaGuardada} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useBorrarVista, useCrearVista, useEditarVista, useVistas} from '../api/vistas';
import {tr} from '../i18n/idioma';

const boton = 'rounded-md border border-linea bg-white px-2.5 py-1 text-xs hover:bg-papel disabled:opacity-50';

// Parametros sin la pagina (una vista no recuerda en que pagina se estaba) y
// en orden fijo, para comparar la pantalla con la vista guardada.
function parametrosDeVista(parametros: URLSearchParams | string): string {
  const copia = new URLSearchParams(parametros);
  copia.delete('pagina');
  copia.sort();
  return copia.toString();
}

const mismaConfiguracion = (a: ConfiguracionDeVista, b: ConfiguracionDeVista) =>
  parametrosDeVista(a.parametros) === parametrosDeVista(b.parametros) &&
  JSON.stringify(a.columnas ?? null) === JSON.stringify(b.columnas ?? null) &&
  JSON.stringify(a.extra ?? {}) === JSON.stringify(b.extra ?? {});

// Vistas guardadas de una pantalla: elegir una, guardar la pantalla como
// vista nueva, guardar cambios sobre la elegida, marcarla para que se abra al
// entrar, cambiarle el nombre o borrarla. Todo queda en el perfil de la
// persona (servidor): la encuentra en cualquier computador al iniciar sesion.
export function BarraDeVistas({
  pantalla,
  actual,
  onAplicar,
}: {
  pantalla: PantallaDeVista;
  // Como esta la pantalla ahora mismo.
  actual: ConfiguracionDeVista;
  onAplicar: (configuracion: ConfiguracionDeVista) => void;
}) {
  const vistas = useVistas(pantalla);
  const crear = useCrearVista(pantalla);
  const editar = useEditarVista(pantalla);
  const borrar = useBorrarVista(pantalla);
  const [activaId, setActivaId] = useState<number | null>(null);
  const [nueva, setNueva] = useState<{nombre: string; predeterminada: boolean} | null>(null);
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  // La vista predeterminada se abre sola solo si se entro a la pantalla sin filtros en la direccion.
  const entroSinFiltros = useRef(typeof window !== 'undefined' && window.location.search === '');
  const predeterminadaAplicada = useRef(false);

  const lista = vistas.data?.vistas ?? [];
  const activa = lista.find(v => v.id === activaId) ?? null;
  const conCambios = activa ? !mismaConfiguracion(activa.configuracion, actual) : false;

  useEffect(() => {
    if (predeterminadaAplicada.current || !vistas.data) return;
    predeterminadaAplicada.current = true;
    const porDefecto = vistas.data.vistas.find(v => v.predeterminada);
    if (porDefecto && entroSinFiltros.current) {
      setActivaId(porDefecto.id);
      onAplicar(porDefecto.configuracion);
    }
  }, [vistas.data, onAplicar]);

  const conError = (error: unknown) =>
    setMensaje(
      error instanceof ErrorDeApi ? error.message : tr('No se pudo guardar la vista.', 'Could not save the view.'),
    );

  const elegir = (id: string) => {
    setMensaje(null);
    setRenombrando(null);
    if (!id) {
      setActivaId(null);
      return;
    }
    const vista = lista.find(v => v.id === Number(id));
    if (!vista) return;
    setActivaId(vista.id);
    onAplicar(vista.configuracion);
  };

  const guardarNueva = async () => {
    if (!nueva?.nombre.trim()) return;
    setMensaje(null);
    try {
      const creada = await crear.mutateAsync({
        nombre: nueva.nombre.trim(),
        configuracion: {...actual, parametros: parametrosDeVista(actual.parametros)},
        predeterminada: nueva.predeterminada,
      });
      setActivaId(creada.id);
      setNueva(null);
    } catch (error) {
      conError(error);
    }
  };

  const cambiar = async (vista: VistaGuardada, cambios: Parameters<typeof editar.mutateAsync>[0]['cambios']) => {
    setMensaje(null);
    try {
      await editar.mutateAsync({id: vista.id, cambios});
      return true;
    } catch (error) {
      conError(error);
      return false;
    }
  };

  const quitar = async (vista: VistaGuardada) => {
    if (!window.confirm(tr(`¿Borrar la vista "${vista.nombre}"?`, `Delete the view "${vista.nombre}"?`))) return;
    setMensaje(null);
    try {
      await borrar.mutateAsync(vista.id);
      setActivaId(null);
    } catch (error) {
      conError(error);
    }
  };

  const ocupado = crear.isPending || editar.isPending || borrar.isPending;

  return (
    <div
      role="region"
      aria-label={tr('Vistas guardadas', 'Saved views')}
      className="flex flex-wrap items-center gap-2 rounded-lg border border-linea bg-white px-3 py-2 text-sm"
    >
      <label className="flex items-center gap-2 text-xs text-apagado">
        {tr('Mis vistas', 'My views')}
        <select
          value={activa ? String(activa.id) : ''}
          onChange={e => elegir(e.target.value)}
          aria-label={tr('Vista guardada', 'Saved view')}
          className="rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
        >
          <option value="">{tr('— Sin vista guardada —', '— No saved view —')}</option>
          {lista.map(v => (
            <option key={v.id} value={v.id}>
              {v.predeterminada ? '★ ' : ''}
              {v.nombre}
            </option>
          ))}
        </select>
      </label>

      {activa && conCambios && (
        <span className="text-xs text-dorado">{tr('Cambios sin guardar', 'Unsaved changes')}</span>
      )}

      {activa && renombrando === null && (
        <>
          {conCambios && (
            <button
              type="button"
              className={boton}
              disabled={ocupado}
              onClick={() =>
                void cambiar(activa, {configuracion: {...actual, parametros: parametrosDeVista(actual.parametros)}})
              }
            >
              {tr('Guardar cambios', 'Save changes')}
            </button>
          )}
          <button
            type="button"
            className={boton}
            disabled={ocupado}
            aria-pressed={activa.predeterminada}
            title={tr(
              'La vista predeterminada se abre sola al entrar a esta pantalla',
              'The default view opens automatically when you enter this screen',
            )}
            onClick={() => void cambiar(activa, {predeterminada: !activa.predeterminada})}
          >
            {activa.predeterminada
              ? tr('★ Se abre al entrar', '★ Opens on entry')
              : tr('☆ Abrir al entrar', '☆ Open on entry')}
          </button>
          <button type="button" className={boton} disabled={ocupado} onClick={() => setRenombrando(activa.nombre)}>
            {tr('Renombrar', 'Rename')}
          </button>
          <button type="button" className={boton} disabled={ocupado} onClick={() => void quitar(activa)}>
            {tr('Borrar', 'Delete')}
          </button>
        </>
      )}

      {activa && renombrando !== null && (
        <form
          className="flex items-center gap-2"
          onSubmit={e => {
            e.preventDefault();
            void cambiar(activa, {nombre: renombrando.trim()}).then(ok => ok && setRenombrando(null));
          }}
        >
          <input
            value={renombrando}
            onChange={e => setRenombrando(e.target.value)}
            maxLength={60}
            autoFocus
            aria-label={tr('Nuevo nombre de la vista', 'New view name')}
            className="rounded-md border border-linea px-2 py-1 text-sm"
          />
          <button type="submit" className={boton} disabled={ocupado || !renombrando.trim()}>
            {tr('Guardar', 'Save')}
          </button>
          <button type="button" className={boton} onClick={() => setRenombrando(null)}>
            {tr('Cancelar', 'Cancel')}
          </button>
        </form>
      )}

      <span className="mx-1 text-linea">|</span>

      {nueva === null ? (
        <button type="button" className={boton} onClick={() => setNueva({nombre: '', predeterminada: false})}>
          {tr('＋ Guardar como vista', '＋ Save as view')}
        </button>
      ) : (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={e => {
            e.preventDefault();
            void guardarNueva();
          }}
        >
          <input
            value={nueva.nombre}
            onChange={e => setNueva({...nueva, nombre: e.target.value})}
            maxLength={60}
            autoFocus
            placeholder={tr('Nombre de la vista', 'View name')}
            aria-label={tr('Nombre de la vista', 'View name')}
            className="rounded-md border border-linea px-2 py-1 text-sm"
          />
          <label className="flex items-center gap-1 text-xs text-apagado">
            <input
              type="checkbox"
              checked={nueva.predeterminada}
              onChange={e => setNueva({...nueva, predeterminada: e.target.checked})}
              className="accent-verde"
            />
            {tr('Abrir al entrar', 'Open on entry')}
          </label>
          <button type="submit" className={boton} disabled={ocupado || !nueva.nombre.trim()}>
            {tr('Guardar', 'Save')}
          </button>
          <button type="button" className={boton} onClick={() => setNueva(null)}>
            {tr('Cancelar', 'Cancel')}
          </button>
        </form>
      )}

      {mensaje && <span className="text-xs text-alerta">{mensaje}</span>}
    </div>
  );
}
